import { type QueryFilters, useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import {
  deleteCoverageRun,
  updateCoverageRunTags,
} from '@/features/codeCoverage/data/coverageRuns';
import { emitCoverageRefresh } from '@/features/codeCoverage/state/coverageRefreshSignal';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { useProjectFilter } from '@/features/projects/hooks/projectHooks';
import { useNotifications } from '@/providers/notificationHooks';
import { getToastMessage } from '@/shared/copy/toasts';
import { useApiErrorMessage } from '@/shared/data/errors';
import { updateTagColor, type TagColor } from '@/shared/data/tags';
import { buildCoverageUploadInvalidations, queryKeys, useInvalidateQueries } from '@/shared/hooks';
import { gridPaths, invalidateGridQuery } from '@/shared/hooks/queryKeys';
import type { CoverageRunDto } from '@flowqa/shared';

type DeleteTarget = {
  id: string;
  name: string;
  projectId: string;
};

type UseCoverageRunTableRowActionsResult = {
  deleteLoading: boolean;
  deleteRowPendingId: string | null;
  deleteTarget: DeleteTarget | null;
  onDeleteClose: () => void;
  onDeleteConfirm: () => void;
  onDeleteRow: (
    row: Pick<CoverageRunDto, 'id' | 'projectId' | 'projectName' | 'commitSha'>,
  ) => void;
  onUpdateRowTagsColor: (input: {
    row: CoverageRunDto;
    tags: string[];
    colors: TagColor[];
  }) => void;
  updateRowTagsColorPendingId: string | null;
};

export const useCoverageRunTableRowActions = (): UseCoverageRunTableRowActionsResult => {
  const queryClient = useQueryClient();
  const invalidateQueries = useInvalidateQueries();
  const { selectedOrganisationId } = useOrganisationFilter();
  const { selectedProjectId } = useProjectFilter();
  const { notify } = useNotifications();
  const toErrorMessage = useApiErrorMessage();
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);
  const [updatingTagRowId, setUpdatingTagRowId] = useState<string | null>(null);

  const deleteCoverageRunMutation = useMutation({
    mutationFn: async (input: { id: string }) => {
      await deleteCoverageRun(input, selectedOrganisationId);
      return input;
    },
    onSuccess: async (_deleted, input) => {
      await invalidateQueries([
        ...buildCoverageUploadInvalidations({
          selectedOrganisationId: selectedOrganisationId ?? null,
          selectedProjectId,
          uploadProjectId: deleteTarget?.projectId ?? null,
        }),
        {
          queryKey: queryKeys.coverageRunDetail(input.id, selectedOrganisationId ?? null),
          exact: false,
        },
        {
          queryKey: queryKeys.coverageFiles(selectedOrganisationId ?? null, selectedProjectId),
          exact: false,
        },
        {
          queryKey: queryKeys.coverageFilesForRun(input.id, selectedOrganisationId ?? null),
          exact: false,
        },
      ]);
      emitCoverageRefresh();
      notify({
        message: getToastMessage('entity.deleted', {
          entity: 'coverage run',
          name: deleteTarget?.name ?? input.id,
        }),
        tone: 'green',
      });
      setDeleteTarget(null);
    },
    onError: (error) => {
      notify({ message: toErrorMessage(error), tone: 'red' });
    },
  });

  const updateCoverageRunTagsMutation = useMutation({
    mutationFn: async (input: { id: string; tags?: string[] }) =>
      await updateCoverageRunTags(input, selectedOrganisationId),
  });

  const updateTagColorMutation = useMutation({
    mutationFn: async (input: { tags: string[]; colors: TagColor[] }) => {
      const updates = input.tags
        .map((name, index) => ({ name: name.trim(), color: input.colors[index] ?? 'neutral' }))
        .filter((item) => item.name.length > 0);
      if (updates.length === 0) {
        return;
      }
      await Promise.all(
        updates.map(async (item) => {
          await updateTagColor(item, selectedOrganisationId ?? undefined);
        }),
      );
    },
  });

  const onDeleteRow = useCallback(
    (row: Pick<CoverageRunDto, 'id' | 'projectId' | 'projectName' | 'commitSha'>) => {
      const name = row.commitSha?.slice(0, 8) ?? row.projectName?.trim() ?? row.id;
      setDeleteTarget({ id: row.id, name, projectId: row.projectId });
    },
    [],
  );

  const onDeleteClose = useCallback(() => {
    if (deleteCoverageRunMutation.isPending) {
      return;
    }
    setDeleteTarget(null);
  }, [deleteCoverageRunMutation.isPending]);

  const onDeleteConfirm = useCallback(() => {
    if (!deleteTarget) {
      return;
    }
    deleteCoverageRunMutation.mutate({ id: deleteTarget.id });
  }, [deleteCoverageRunMutation, deleteTarget]);

  const onUpdateRowTagsColor = useCallback(
    ({ row, tags, colors }: { row: CoverageRunDto; tags: string[]; colors: TagColor[] }) => {
      const normalizedTags = Array.from(
        new Set(tags.map((tag) => tag.trim()).filter((tag) => tag.length > 0)),
      );
      const normalizedColors = colors.slice(0, normalizedTags.length);
      setUpdatingTagRowId(row.id);
      void (async () => {
        try {
          await updateCoverageRunTagsMutation.mutateAsync({ id: row.id, tags: normalizedTags });
          if (normalizedTags.length > 0) {
            await updateTagColorMutation.mutateAsync({
              tags: normalizedTags,
              colors: normalizedColors,
            });
          }
          notify({ message: getToastMessage('tags.updated'), tone: 'green' });
          await queryClient.invalidateQueries({
            queryKey: queryKeys.tagOptions(selectedProjectId, selectedOrganisationId ?? null),
          });
          const invalidations: QueryFilters[] = [
            {
              queryKey: queryKeys.coverageRuns(selectedOrganisationId ?? null, selectedProjectId),
              exact: false,
            },
            {
              queryKey: queryKeys.coverageRunSummary(
                selectedOrganisationId ?? null,
                selectedProjectId,
              ),
              exact: false,
            },
            {
              queryKey: queryKeys.coverageRunDetail(row.id, selectedOrganisationId ?? null),
              exact: false,
            },
            invalidateGridQuery(gridPaths.coverageRuns),
          ];
          await invalidateQueries(invalidations);
          await queryClient.refetchQueries({ type: 'active' });
        } catch (error) {
          notify({ message: toErrorMessage(error), tone: 'red' });
        } finally {
          setUpdatingTagRowId(null);
        }
      })();
    },
    [
      invalidateQueries,
      notify,
      queryClient,
      selectedOrganisationId,
      selectedProjectId,
      toErrorMessage,
      updateCoverageRunTagsMutation,
      updateTagColorMutation,
    ],
  );

  return {
    deleteLoading: deleteCoverageRunMutation.isPending,
    deleteRowPendingId: deleteCoverageRunMutation.isPending ? (deleteTarget?.id ?? null) : null,
    deleteTarget,
    onDeleteClose,
    onDeleteConfirm,
    onDeleteRow,
    onUpdateRowTagsColor,
    updateRowTagsColorPendingId:
      updateTagColorMutation.isPending || updateCoverageRunTagsMutation.isPending
        ? updatingTagRowId
        : null,
  };
};
